import { IPD_MEAL_LABELS, parseIpdTaskLabel, type IpdTaskDisplayStatus, type IpdTaskOutcome, type IpdTaskView } from '@his/shared';
import { tr } from '@/lib/i18n-core';
import type { StatusTone } from '@/components/ui/primitives';
import { readingFieldsFor, type ReadingField } from './record-task';

const TONES: Readonly<Record<IpdTaskDisplayStatus, StatusTone>> = {
  upcoming: 'info',
  due: 'warning',
  overdue: 'destructive',
  missed: 'destructive',
  given: 'success',
  given_late: 'warning',
  held: 'muted',
  refused: 'destructive',
  not_given: 'muted',
};

export const CHART_LEGEND: readonly IpdTaskDisplayStatus[] = ['upcoming', 'due', 'overdue', 'missed', 'given', 'given_late', 'held', 'refused', 'not_given'];

export function taskStatusTone(status: IpdTaskDisplayStatus): StatusTone {
  return TONES[status] ?? 'secondary';
}

export function taskStatusLabel(status: IpdTaskDisplayStatus): string {
  return tr('ipd', `io_status_${status}`);
}

export function taskKindLabel(taskKind: string): string {
  return tr('ipd', `io_task_kind_${taskKind}`);
}

export function orderKindLabel(kind: string): string {
  return tr('ipd', `io_kind_${kind}`);
}

export function outcomeLabelKey(outcome: IpdTaskOutcome, taskKind: string): string {
  const dose = taskKind === 'dose';
  if (outcome === 'given') return dose ? 'io_outcome_given' : 'io_outcome_done';
  if (outcome === 'not_given') return dose ? 'io_outcome_not_given' : 'io_outcome_not_done';
  return `io_outcome_${outcome}`;
}

export function outcomeLabel(outcome: IpdTaskOutcome, taskKind: string): string {
  return tr('ipd', outcomeLabelKey(outcome, taskKind));
}

export function taskPartLabel(label: string | null | undefined): string | null {
  const part = parseIpdTaskLabel(label);
  if (part.bag !== undefined) return tr('ipd', 'io_task_bag', { bag: part.bag });
  if (part.unit !== undefined && part.step) return tr('ipd', 'io_task_unit_step', { unit: part.unit, step: tr('ipd', `io_task_step_${part.step}`) });
  if (part.initial) return tr('ipd', 'io_task_initial');
  if (part.meal) {
    if ((IPD_MEAL_LABELS as readonly string[]).includes(part.meal)) return tr('ipd', `io_task_meal_${part.meal}`);
    const index = /^meal-(\d+)$/.exec(part.meal)?.[1];
    return index ? tr('ipd', 'io_task_meal_n', { number: index }) : null;
  }
  return null;
}

export function taskTitle(task: Pick<IpdTaskView, 'taskKind' | 'label'>): string {
  const part = taskPartLabel(task.label);
  return part ? `${taskKindLabel(task.taskKind)} · ${part}` : taskKindLabel(task.taskKind);
}

export const READING_LABEL_KEYS: Readonly<Record<ReadingField, string>> = {
  bpSystolic: 'io_task_reading_bp_systolic',
  bpDiastolic: 'io_task_reading_bp_diastolic',
  pulse: 'io_task_reading_pulse',
  respiratoryRate: 'io_task_reading_respiratory_rate',
  temperatureC: 'io_task_reading_temperature',
  spo2: 'io_task_reading_spo2',
  painScore: 'io_task_reading_pain',
  bloodSugarMgDl: 'io_task_reading_blood_sugar',
  flowLpm: 'io_task_reading_flow',
  fio2: 'io_task_reading_fio2',
  rateMlPerHour: 'io_task_reading_rate',
  volumeInfusedMl: 'io_task_reading_volume',
  consciousness: 'io_task_reading_consciousness',
  siteCondition: 'io_task_reading_site',
  device: 'io_task_reading_device',
  reaction: 'io_task_reading_reaction',
  reactionNotes: 'io_task_reading_reaction_notes',
};

export function readingLabel(field: ReadingField): string {
  return tr('ipd', READING_LABEL_KEYS[field]);
}

export function consciousnessLabel(level: string): string {
  return tr('ipd', `io_task_avpu_${level}`);
}

export function siteConditionLabel(condition: string): string {
  return tr('ipd', `io_task_site_${condition}`);
}

export function deviceLabel(device: string): string {
  return tr('ipd', `io_task_device_${device}`);
}

export function readingValueText(field: ReadingField, value: unknown): string | null {
  if (value === undefined || value === null || value === '') return null;
  if (field === 'consciousness') return consciousnessLabel(String(value));
  if (field === 'siteCondition') return siteConditionLabel(String(value));
  if (field === 'device') return deviceLabel(String(value));
  if (field === 'reaction') return tr('ipd', value === true ? 'io_task_reaction_seen' : 'io_task_reaction_none');
  return String(value);
}

export function readingLines(taskKind: string, value: unknown): { field: ReadingField; label: string; text: string }[] {
  if (!value || typeof value !== 'object') return [];
  const source = value as Record<string, unknown>;
  const known = readingFieldsFor(taskKind);
  const fields = known.length > 0 ? known : (Object.keys(READING_LABEL_KEYS) as ReadingField[]);
  const lines: { field: ReadingField; label: string; text: string }[] = [];
  for (const field of fields) {
    const text = readingValueText(field, source[field]);
    if (text !== null) lines.push({ field, label: readingLabel(field), text });
  }
  return lines;
}
